import { ReactElement, useState, useEffect } from 'react'
import { PurchaseOrder, PurchaseOrderLine} from './api/purchaseorder'
import { SalesOrder, SalesOrderLine} from './api/salesorder'
import { getPurchaseOrderEOURL, getSalesOrderEOURL } from './links'
import { ISODate } from './date'
import './OrderLinesTable.scss'
import {ShippingAggregationButtons} from "./ShippingAggregationButtons"
import { AddPrefixToObject} from "./tsmagic"
import {useDivision} from "./shared"


// Necessary because of https://stackoverflow.com/questions/52926147/
type RO_Array<PS extends PurchaseOrderLine | SalesOrderLine> =
  PS extends PurchaseOrderLine ? readonly PurchaseOrderLine[] :
  readonly SalesOrderLine[]

type PurchaseOrderSortables = Pick<PurchaseOrder,
  "orderNumber" | "supplierName" | "orderDate" | "warehouseDescription">
type SalesOrderSortables = Pick<SalesOrder,
  "orderNumber" | "orderedByName" | "orderDate" | "warehouseDescription">
type PurchaseOrderLineSortables = Pick<PurchaseOrderLine,
     "itemCode" | "description" | "quantity" | "receiptDate" | "lineNumber"> &
       {shipping: PurchaseOrderLine["$calc"]["shipping"]}
type SalesOrderLineSortables = Pick<SalesOrderLine,
  "itemCode" | "description" | "quantity" | "deliveryDate" | "lineNumber">

type OrderLineSortables<PS extends PurchaseOrderLine | SalesOrderLine> =
  PS extends PurchaseOrderLine ? PurchaseOrderLineSortables :
  PS extends SalesOrderLine ? SalesOrderLineSortables :
  never

type SortableColumnTypes<PS extends SalesOrderLine | PurchaseOrderLine> =
  PS extends PurchaseOrderLine ? (
    AddPrefixToObject<PurchaseOrderSortables, "purchaseOrder.">
    & PurchaseOrderLineSortables
) : (
    AddPrefixToObject<SalesOrderSortables, "salesOrder."> & SalesOrderLineSortables
)

type TypeName<PS extends SalesOrderLine | PurchaseOrderLine> =
  PS extends PurchaseOrderLine ? "purchase" : "sales"

export type OrderLinesTableProps<PS extends PurchaseOrderLine | SalesOrderLine> = (
  PS extends PurchaseOrderLine ? {
  items: readonly PurchaseOrderLine[]
} : {
  items: readonly SalesOrderLine[]
})

type SortableColumn<PS extends PurchaseOrderLine | SalesOrderLine> =
  keyof SortableColumnTypes<PS> & string

type SortOrderItem<PS extends PurchaseOrderLine | SalesOrderLine,
                   T extends SortableColumn<PS>> = {
  column: T
  reverse: boolean,
}

type SortOrder<PS extends PurchaseOrderLine | SalesOrderLine> =
  readonly SortOrderItem<PS, SortableColumn<PS>>[]

function DEFAULT_SORT_ORDER<PS extends PurchaseOrderLine | SalesOrderLine>(
      type: TypeName<PS>): SortOrder<PS> {
  if (type === "purchase") {
    return [
      {column: "purchaseOrder.orderDate", reverse: false},
      {column: "purchaseOrder.orderNumber", reverse: false},
      {column: "lineNumber", reverse: false},
    ] as SortOrder<PS>
  }
  if (type === "sales") {
    return [
      {column: "salesOrder.orderDate", reverse: false},
      {column: "salesOrder.orderNumber", reverse: false},
      {column: "lineNumber", reverse: false},
    ] as SortOrder<PS>
  }
  return undefined as never
}

function nullableStringCompare(a: string | null, b: string | null): number {
  return (
    a === b ? 0
      : a === null ? -1
        : b === null ? 1
          : a.localeCompare(b)
  )
}
type CompareMethod<T> = (a: T, b: T) => number
type CompareMethodsMap<PS extends PurchaseOrderLine | SalesOrderLine> = {
  [K in SortableColumn<PS>]: CompareMethod<SortableColumnTypes<PS>[K]>}

function COMPARE_METHODS<PS extends PurchaseOrderLine | SalesOrderLine>(
type: TypeName<PS>): CompareMethodsMap<PS> {
  if (type === "purchase") {
    return {
      "purchaseOrder.orderNumber": (a, b) => a - b,
      "purchaseOrder.orderDate": ISODate.compare,
      "purchaseOrder.supplierName": nullableStringCompare,
      "purchaseOrder.warehouseDescription": nullableStringCompare,
      itemCode: nullableStringCompare,
      description: nullableStringCompare,
      quantity: (a, b) => a - b,
      receiptDate: ISODate.compare,
      lineNumber: (a, b) => a - b,
      shipping: (a, b) => nullableStringCompare(a.join(", "), b.join(", ")),
    } as CompareMethodsMap<PurchaseOrderLine> as CompareMethodsMap<PS>
  }
  if (type === "sales") {
    return {
      "salesOrder.orderNumber": (a, b) => a - b,
      "salesOrder.orderDate": ISODate.compare,
      "salesOrder.orderedByName": nullableStringCompare,
      "salesOrder.warehouseDescription": nullableStringCompare,
      itemCode: nullableStringCompare,
      description: nullableStringCompare,
      quantity: (a, b) => a - b,
      deliveryDate: ISODate.compare,
      lineNumber: (a, b) => a - b,
    } as CompareMethodsMap<SalesOrderLine> as CompareMethodsMap<PS>
  }
  return undefined as never
}

function sort<PS extends PurchaseOrderLine | SalesOrderLine>(
  type: TypeName<PS>,
  items: RO_Array<PS>,
  sortOrder: SortOrder<PS>,
  setSortOrderDepth: (sortOrderDepth: number) => void
): RO_Array<PS> {
    let sortOrderDepth = 0
    const result = [...items]
      .sort((a, b) => {
        // needs to be here, cannot make Generic anonymous functions in TSX:
        // https://stackoverflow.com/questions/50611757/how-do-you-pass-a-generic-type-to-an-anonymous-function-in-typescript-tsx-file
        function sortOrderReduction<T extends SortableColumn<PS>>(
            acc: number, {column, reverse}: SortOrderItem<PS, T>, index: number
        ): number {
          if (acc !== 0) {
            return acc
          }
          sortOrderDepth = Math.max(sortOrderDepth, index)
          // type ColumnType = SortableColumnTypes<PS>[T]
          type ColumnType = string | number | ISODate
          const retriever: (ol: PS) => ColumnType = (ol) => {
            if (ol instanceof PurchaseOrderLine
                && column.startsWith("purchaseOrder.")) {
              const columnname =
                column.slice("purchaseOrder.".length) as keyof PurchaseOrderSortables
              return ol.parent[columnname] as ColumnType
            } else if (ol instanceof SalesOrderLine
                       && column.startsWith("salesOrder.")) {
              const columnname =
                column.slice("salesOrder.".length) as keyof SalesOrderSortables
              return ol.parent[columnname] as ColumnType
            } else {
              // some dirty TS needed to make this work
              const columnname = column as keyof OrderLineSortables<PS> & keyof PS
              return ol[columnname] as never as ColumnType
            }
          }
          const a_item: ColumnType = retriever(a as PS)
          const b_item: ColumnType = retriever(b as PS)
          const cmp_methods = COMPARE_METHODS(type)
          const cmp = cmp_methods[column] as unknown as CompareMethod<ColumnType>
          return (reverse ? -1 : 1) * cmp(a_item, b_item)
        }
        return sortOrder.reduce<number>(sortOrderReduction, 0)
      })
    setSortOrderDepth(sortOrderDepth)
    return result as RO_Array<PS>
}

function handleHeaderClick<PS extends PurchaseOrderLine | SalesOrderLine>(
    column: SortableColumn<PS>,
    sortOrder: SortOrder<PS>,
    setSortOrder: (sortOrder: SortOrder<PS>) => void): void {
  const reverse = (sortOrder[0]!.column === column && !sortOrder[0]!.reverse)
  setSortOrder([
    {column, reverse},
    ...sortOrder.filter(item => item.column !== column)
  ])
}

function GenericHeaderCell<PS extends PurchaseOrderLine | SalesOrderLine>(
    {column, label, sortOrder, setSortOrder, sortOrderDepth}:
    {column: SortableColumn<PS>, label: string, sortOrder: SortOrder<PS>,
      setSortOrder (sortOrder: SortOrder<PS>): void, sortOrderDepth: number,
    }
): ReactElement {
  const index = sortOrder.findIndex(so => so.column === column)
  if (index === -1 || index > sortOrderDepth) {
    return <th className="sortableHeader" onClick={
      () => handleHeaderClick(column, sortOrder, setSortOrder)}>{label}</th>
  }
  return (
    <th className="sortableHeader" onClick={
      () => handleHeaderClick(column, sortOrder, setSortOrder)}>
      {label}
      <span className="sortIcon">
        {sortOrder[index].reverse ? <span>&#x25BC;</span> : <span>&#x25B2;</span>}
        <sup>{index + 1}</sup>
      </span>
    </th>
  )
}

export function PurchaseOrderLinesTable(
    props: OrderLinesTableProps<PurchaseOrderLine>): ReactElement {
  const {items } = props
  const division = useDivision()
  const [sortOrder, setSortOrder] = useState<SortOrder<PurchaseOrderLine>>(
    DEFAULT_SORT_ORDER("purchase"))
  const [sortedItems, setSortedItems] =
    useState<readonly PurchaseOrderLine[] | undefined>(undefined)

  // this keeps track of how deep the sortOrder has any influence
  const [sortOrderDepth, setSortOrderDepth] = useState<number>(0)

  useEffect(() => {
    setSortedItems(sort("purchase", items, sortOrder, setSortOrderDepth))
  }, [sortOrder, items])

  if (sortedItems === undefined) {
    return <div>Loading</div>
  }

  const HeaderCell =(
    {column, label}: {column: SortableColumn<PurchaseOrderLine>, label: string}
  ) => GenericHeaderCell<PurchaseOrderLine>(
    {column,
      label,
      sortOrder,
      setSortOrder,
      sortOrderDepth})

    return (
      <table className="OrderLinesTable">
        <thead>
          <tr>
            <HeaderCell column="purchaseOrder.orderDate" label="PO date" />
            <HeaderCell column="purchaseOrder.orderNumber" label="POnr" />
            <HeaderCell column="lineNumber" label="Row nr" />
            <HeaderCell column="purchaseOrder.supplierName" label="Supplier" />
            <HeaderCell column="itemCode" label="Item" />
            <HeaderCell column="description" label="Description" />
            <HeaderCell column="quantity" label="Quantity" />
            <HeaderCell column="receiptDate" label="Receipt Date" />
            <HeaderCell column="purchaseOrder.warehouseDescription"
              label="Warehouse" />
            <HeaderCell column="shipping" label="Shipping" />
          </tr>
        </thead>
        <tbody>
          {sortedItems.length === 0 && 
          <tr>
            <td colSpan={9}>-----------</td>
          </tr>
          }
          {sortedItems.map(pol => {
            return (
              <tr key={pol.parent.purchaseOrderID + ";" + pol.lineNumber.toString()}>
                <td className="date">{pol.parent.orderDate.toDDMMYYYYString()}</td>
                <td className="number">
                  <a href={getPurchaseOrderEOURL(
                      division, pol.parent.purchaseOrderID).toString()}
                    target="_blank" rel="noreferrer">
                    {pol.parent.orderNumber}
                  </a>
                </td>
                <td className="number">{pol.lineNumber}</td>
                <td className="oneliner">{pol.parent.supplierName}</td>
                <td className="oneliner">{pol.itemCode}</td>
                <td className="oneliner">{pol.description}</td>
                <td className="number">{pol.quantity}</td>
                <td className="date">{pol.receiptDate.toDDMMYYYYString()}</td>
                <td className="oneliner">{pol.parent.warehouseDescription}</td>
                <td>
                  <ShippingAggregationButtons
                    shippingAggregates={pol.$calc.shipping} />
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    )
  }

export function SalesOrderLinesTable(
    props: OrderLinesTableProps<SalesOrderLine>): ReactElement {
  const {items } = props
  const division = useDivision()
  const [sortOrder, setSortOrder] = useState<SortOrder<SalesOrderLine>>(
    DEFAULT_SORT_ORDER("sales"))
  const [sortedItems, setSortedItems] =
    useState<readonly SalesOrderLine[] | undefined>(undefined)

  // this keeps track of how deep the sortOrder has any influence
  const [sortOrderDepth, setSortOrderDepth] = useState<number>(0)

  useEffect(() => {
    setSortedItems(sort("sales", items, sortOrder, setSortOrderDepth))
  }, [sortOrder, items])

  if (sortedItems === undefined) {
    return <div>Loading</div>
  }

  const HeaderCell =(
    {column, label}: {column: SortableColumn<SalesOrderLine>, label: string}
  ) => GenericHeaderCell<SalesOrderLine>(
    {column,
      label,
      sortOrder,
      setSortOrder,
      sortOrderDepth})

    return (
      <table className="OrderLinesTable">
        <thead>
          <tr>
            <HeaderCell column="salesOrder.orderDate" label="PO date" />
            <HeaderCell column="salesOrder.orderNumber" label="POnr" />
            <HeaderCell column="lineNumber" label="Row nr" />
            <HeaderCell column="salesOrder.orderedByName" label="Supplier" />
            <HeaderCell column="itemCode" label="Item" />
            <HeaderCell column="description" label="Description" />
            <HeaderCell column="quantity" label="Quantity" />
            <HeaderCell column="deliveryDate" label="Receipt Date" />
            <HeaderCell column="salesOrder.warehouseDescription"
              label="Warehouse" />
          </tr>
        </thead>
        <tbody>
          {sortedItems.length === 0 && 
          <tr>
            <td colSpan={9}>-----------</td>
          </tr>
          }
          {sortedItems.map(pol => {
            return (
              <tr key={pol.parent.orderID + ";" + pol.lineNumber.toString()}>
                <td className="date">{pol.parent.orderDate.toDDMMYYYYString()}</td>
                <td className="number">
                  <a href={getSalesOrderEOURL(division, pol.parent.orderID).toString()} target="_blank" rel="noreferrer">
                    {pol.parent.orderNumber}
                  </a>
                </td>
                <td className="number">{pol.lineNumber}</td>
                <td className="oneliner">{pol.parent.orderedByName}</td>
                <td className="oneliner">{pol.itemCode}</td>
                <td className="oneliner">{pol.description}</td>
                <td className="number">{pol.quantity}</td>
                <td className="date">{pol.deliveryDate.toDDMMYYYYString()}</td>
                <td className="oneliner">{pol.parent.warehouseDescription}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    )
}
